import { useContext } from 'react';
import { useLocales } from '@messageformat/react';
import Api from '../api';
import { InsuranceOfferContext } from '../context';
import { analytics } from '../utils';
import { ValidateSurveyQuestions } from '../validators/ValidateSurveyQuestions';

export const NewInsuranceOfferRequest = () => {
  const [language] = useLocales();
  const [state, setState] = useContext(InsuranceOfferContext);
  const { visitorAction } = analytics();
  const { selectedInsurances, contactInfo } = state;

  const replaceSpecialCharacters = (products) => {
    let chars = { ö: 'o', Ö: 'o', ä: 'a', å: 'a', Ä: 'a', Å: 'a' };
    let productListFinal = products;
    productListFinal = productListFinal.replace(/[öÖäåÄÅ]/g, (m) => chars[m]);
    productListFinal = productListFinal.replace(/,\s*$/, '');
    productListFinal = productListFinal.toLowerCase();
    return productListFinal.replace(/evar/g, 'eVar');
  };

  const getProductQuestion = (question, type, key) =>
    question ? `${key}${question[type]}` : ``;
  const getProductQuestionLife = (questionOne, questionTwo, questionThree, type, key) => {
    const sanitize = (s) =>
      String(s || '')
        .replace(/[äÄ]/g, 'a')
        .replace(/[öÖ]/g, 'o')
        .replace(/\s+/g, '_');

    const toInclude = (q) => q && q.defaultAnswer === 'Kyllä' && q[type];

    const items = [questionOne, questionTwo, questionThree]
      .filter(toInclude)
      .map((q) => sanitize(q[type]));

    // Always include key (e.g., "|eVar67=") even when there are no items
    return `${key}${items.join('-')}`;
  };
  const getPersonAnswers = (questions, key, type) =>
    `eVar66=${questions[key].defaultAnswer}${getProductQuestion(
      questions[type],
      'defaultQuestion',
      '|eVar59='
    )}`;

  const childProducts = (questions, key, defaultCaption, count) => {
    return questions[key].value === 'Child'
      ? `vakuutukset;${defaultCaption};${count};;;eVar66=${
          questions[key].defaultAnswer
        }|eVar59=terveysvakuutus${getProductQuestion(
          questions['insuranceScopeChild'],
          'value',
          '|eVar67='
        )}`
      : '';
  };
  const getunbornProducts = (questions) => {
    return questions['motherChild'].value === 'Yes'
      ? 'tapaturmat ja sairaudet-vakuutuksenottajaaiti'
      : 'tapaturmat ja sairaudet-vakuutuksenottajaeiaiti';
  };
  const unbornProducts = (questions, key, defaultCaption, count) => {
    return questions[key].value === 'Unborn child or twins'
      ? `vakuutukset;${defaultCaption};${count};;;eVar66=${
          questions[key].defaultAnswer
        }|eVar59=terveysvakuutus|eVar67=${getunbornProducts(questions)}`
      : '';
  };

  const getProductDetails = () => {
    const getPersonProducts = (questions, key, defaultCaption, count) => {
      const anotherAdult = questions[key].value === 'Another adult'
      let life = '';
      let healthCoverage = '';
      let child = '';
      let unborn = '';
      if (
        questions[key].value !== 'Child' &&
        questions[key].value !== 'Unborn child or twins'
      ) {
        if (questions['Life']?.value === 'Yes') {
          life = `vakuutukset;${defaultCaption};${count};;;${getPersonAnswers(
            questions,
            key,
            'Life'
          )}${getProductQuestionLife(
            questions['AdditionalLifeInsurancesSeriousIllness'],
            questions['AdditionalLifeInsurancesTemporaryDisability'],
            questions['AdditionalLifeInsurancesPermanentDisability'],
            'defaultQuestion',
            '|eVar67='
          )}`;
        }
        if (questions['health']?.value === 'Yes'|| anotherAdult) {
          healthCoverage = `,vakuutukset;${defaultCaption};${count};;;${getPersonAnswers(
            questions,
            key,
            'health'
          )}${getProductQuestion(
            questions['healthCoverage'],
            'value',
            '|eVar67='
          )}`;
        }
      }
      child = childProducts(questions, key, defaultCaption, count);
      unborn = unbornProducts(questions, key, defaultCaption, count);
      let personAnswers = life + healthCoverage + child + unborn;
      personAnswers = personAnswers.replace(/^,/, '');
      return personAnswers === ''
        ? `vakuutukset;${defaultCaption};${count};;;eVar66=${questions[key].defaultAnswer}`
        : personAnswers;
    };

    const getProductAnswer = ({ type, questions, defaultCaption, count }) => {
      const key = `${type}Type`;
      return type === 'person'
        ? getPersonProducts(questions, key, defaultCaption, count)
        : `vakuutukset;${defaultCaption};${count};;;eVar66=${questions[key].defaultAnswer}`;
    };

    const productList = selectedInsurances.reduce(
      (list, { type, defaultCaption, questions }) => {
        const count = 1;
        list.push(
          getProductAnswer({
            type,
            questions,
            defaultCaption,
            count,
          })
        );
        return list;
      },
      []
    );

    return replaceSpecialCharacters(productList.join(','));
  };

  const getProductData = () => {
    const insuranceCountMap = selectedInsurances.reduce(
      (accumulator, record) => {
        accumulator[record.defaultCaption] =
          (accumulator[record.defaultCaption] || 0) + 1;
        return accumulator;
      },
      {}
    );

    const counter = Object.keys(insuranceCountMap).reduce(
      (accumulator, record) =>
        `${accumulator}vakuutukset;${record};${insuranceCountMap[record]};;;,`,
      ''
    );

    return replaceSpecialCharacters(counter);
  };

  const getBaseName = (getMessage) => {
    const prefix =
      (window.location.pathname.split('/', 2).pop() || '').toLowerCase() ===
      language
        ? `/${language}`
        : '';
    return process.env.NODE_ENV === 'development'
      ? ''
      : `${prefix}${getMessage('url.baseName')}`;
  };

  const getPathName = (getMessage) => {
    return `${getBaseName(getMessage)}${getMessage('url.thankyoupage')}`;
  };

  const sendReport = async (navigate, setSubmitActive, getMessage) => {
    // only sending those questions where sendInsurance is true
    const surveys = selectedInsurances.filter((item) => item.sendInsurance).map(
      ({ type, caption, questions, id }) => ({
        id,
        name: type,
        title: caption,
        questions: ValidateSurveyQuestions({
          type,
          questions: { ...questions },
        }),
      })
    );

    const personalData = {
      ...contactInfo, 
      firstName: contactInfo.firstName.trim(),
      lastName: contactInfo.lastName.trim(),
    }

    const requestBody = { surveys, personalData };

    const getErrorAction = (error) => {
      visitorAction({
        phase: '3. vakuutuksen ottajan tiedot',
        phaseCategory: '',
        pageName: 'vakuutuksen-ottajan-tiedot',
        error,
        language,
        products: getProductDetails(),
      });
      const action = { type: 'IO_CHANGE_SHOWEXCEPTIONMESSAGE' };
      setState({ state, ...action }, action);
      document.getElementById('alert').scrollIntoView({ behavior: 'smooth' });
    };
    try {
      const response = await Api.processSurvey(requestBody);
      if (response?.status === 'OK') {
        setSubmitActive(false);
        window.scrollTo(0, 0);
        navigate(getPathName(getMessage), {
          state: { products: getProductDetails() },
        });
        navigate(0);
      } else {
        getErrorAction({ message: 'no response found' });
        setSubmitActive(false);
      }
    } catch (err) {
      getErrorAction(err);
      setSubmitActive(false);
    }
  };

  const sendNewReport = (navigate, setSubmitActive, getMessage) => {
    return sendReport(navigate, setSubmitActive, getMessage);
  };

  return {
    state,
    sendNewReport,
    getProductDetails,
    getProductData,
  };
};
